/**
 * CG-7 — `usePersistAccountRole` hook.
 *
 * Persists the onboarding step-0 fork choice (`_account_role`) to the
 * server. Same atomic patch-POST pattern as `usePersistOnboarding`
 * (validate against FIELD_SPECS → single `POST /api/context/:userId`
 * with `patch` → local mergeContext), and the same throw-on-failure
 * semantics — the fork CTA surfaces an inline error and stays enabled
 * for retry, because losing the guardian intent would drop the user
 * back into athlete onboarding on their next visit.
 *
 * B209 (2026-09-20, "band on the coach path") — `persistAgeBand` is a
 * second, narrower persist on the SAME door: the age step (REL-29) always
 * precedes the coach fork, so a coach has already ANSWERED the birth pair
 * by the time `CoachOnboarding` mounts, but nothing wrote it to the
 * server. With `age_band_required_v1` on, the unknown-age wall
 * (`checkCoppaGate`) then refuses a brand-new coach's first
 * `POST /api/teams` — they own no team yet, so the membership-backed
 * exemption cannot apply. Persisting the pair here, before the team
 * create, gives the wall an established age and closes the deadlock
 * without inventing a second, self-claimed exemption. Same validate →
 * POST → mergeContext shape as `persistAccountRole`, and the same
 * house error-with-`code` shape `usePersistOnboarding` throws (spec
 * §4.8 / `lib/planEdit`'s `editAgeBelowMinimum`) — a caller that renders
 * the ratified refusal line reads `err.code`, not `err.message`.
 */

import { useCallback } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { validateField } from '../lib/settingsPersist';
import { useAthleteContext } from './useAthleteContext';
import { useAuthFetch } from './useAuthFetch';
import { deriveAge } from './usePersistOnboarding';

export type AccountRole = 'athlete' | 'guardian' | 'coach';

export interface UsePersistAccountRoleResult {
  /** Persist the fork choice. Resolves on success; rejects on failure. */
  persistAccountRole: (role: AccountRole) => Promise<void>;
  /**
   * B209 — persist the wizard's already-answered birth pair. Resolves on
   * success; rejects on failure with the server's `code` attached (e.g.
   * `age_below_minimum`, the RULE 1 floor) when the response carries one.
   */
  persistAgeBand: (birthYear: number, birthMonth: number) => Promise<void>;
}

export function usePersistAccountRole(): UsePersistAccountRoleResult {
  const { user } = useAuthContext();
  const fetchAuthed = useAuthFetch();
  const { mergeContext } = useAthleteContext();

  const persistAccountRole = useCallback(
    async (role: AccountRole) => {
      const validation = validateField('_account_role', role);
      if (!validation.ok) {
        throw new Error(`usePersistAccountRole: invalid role: ${validation.error}`);
      }
      if (!user) {
        throw new Error('Not signed in');
      }
      // B237 — patch-only. The server always merges a body with no
      // `context` key (B148 ruling); see `usePersistOnboarding`'s comment.
      const patch = { _account_role: validation.value };
      const res = await fetchAuthed(`/api/context/${encodeURIComponent(user.id)}`, {
        method: 'POST',
        body: JSON.stringify({ patch }),
      });
      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`);
      }
      mergeContext(patch);
    },
    [user, fetchAuthed, mergeContext],
  );

  const persistAgeBand = useCallback(
    async (birthYear: number, birthMonth: number) => {
      const yearValidation = validateField('_birth_year', birthYear);
      if (!yearValidation.ok) {
        throw new Error(`usePersistAccountRole: invalid birth year: ${yearValidation.error}`);
      }
      const monthValidation = validateField('_birth_month', birthMonth);
      if (!monthValidation.ok) {
        throw new Error(`usePersistAccountRole: invalid birth month: ${monthValidation.error}`);
      }
      const ageValidation = validateField('Age', deriveAge(birthYear, birthMonth));
      if (!ageValidation.ok) {
        throw new Error(`usePersistAccountRole: invalid derived age: ${ageValidation.error}`);
      }
      if (!user) {
        throw new Error('Not signed in');
      }
      // B237 — patch-only (see `persistAccountRole`'s comment above).
      const patch = {
        _birth_year: yearValidation.value,
        _birth_month: monthValidation.value,
        Age: ageValidation.value,
      };
      const res = await fetchAuthed(`/api/context/${encodeURIComponent(user.id)}`, {
        method: 'POST',
        body: JSON.stringify({ patch }),
      });
      if (!res.ok) {
        // B196 L5/L9 house pattern (usePersistOnboarding.ts) — carry the
        // server's refusal code (`age_below_minimum`, the RULE 1 floor) on
        // the thrown Error so the caller can render the ratified sentence
        // (`lib/planEdit`'s `editAgeBelowMinimum` + `AGE_BELOW_MINIMUM_MESSAGE`)
        // instead of a generic retry line.
        let code: string | undefined;
        try {
          const body = await res.json();
          if (body && typeof body.code === 'string') code = body.code;
        } catch {
          /* ignore body parse errors */
        }
        const err: Error & { code?: string } = new Error(`Server returned ${res.status}`);
        if (code) err.code = code;
        throw err;
      }
      mergeContext(patch);
    },
    [user, fetchAuthed, mergeContext],
  );

  return { persistAccountRole, persistAgeBand };
}
